import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Building2, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ClaimBusiness() {
  const [step, setStep] = useState(1);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ full_name: "", role_in_business: "Owner", business_email: "", business_phone: "", verification_method: "phone", notes: "" });
  const nav = useNavigate();

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/providers/search-businesses?q=${encodeURIComponent(q)}`);
      setResults(data);
    } finally {
      setSearching(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/claims", { provider_id: selected.id, ...form });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit claim");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <CheckCircle2 className="w-16 h-16 text-[#10B981] mx-auto mb-6" />
        <h1 className="font-display text-3xl font-semibold mb-3">Claim submitted!</h1>
        <p className="text-stone-600 mb-8">Our team reviews claim requests within 24–48 hours. We'll email you at your account address once approved.</p>
        <div className="flex gap-3 justify-center">
          <Button asChild variant="outline"><Link to="/dashboard/user">View my account</Link></Button>
          <Button asChild className="bg-[#064E3B] hover:bg-[#053d2f]"><Link to="/">Back to home</Link></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-2">Claim your business</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold mb-2">Are you the owner?</h1>
        <p className="text-stone-600">Claim your existing listing to manage photos, respond to reviews, and receive appointment leads.</p>
      </div>

      {step === 1 && (
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h2 className="font-display text-lg font-semibold mb-1">Step 1 — Find your business</h2>
          <p className="text-sm text-stone-500 mb-4">Search by business name or city.</p>
          <div className="flex gap-2 mb-4">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="e.g. Pacific Coast IV Lounge" data-testid="claim-search-input" />
            <Button onClick={search} disabled={searching} className="bg-[#064E3B] hover:bg-[#053d2f]" data-testid="claim-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>

          {results.length > 0 && (
            <div className="space-y-2 mb-4">
              {results.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { setSelected(p); setStep(2); }}
                  disabled={!!p.owner_id}
                  className={`w-full text-left p-4 rounded-xl border transition ${p.owner_id ? "bg-stone-50 border-stone-200 opacity-60 cursor-not-allowed" : "bg-white border-stone-200 hover:border-[#064E3B]/40 hover:shadow-sm"}`}
                  data-testid={`claim-result-${p.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display font-semibold">{p.name}</div>
                      <div className="text-xs text-stone-500">{p.city}, {p.state} · {p.phone}</div>
                    </div>
                    {p.owner_id ? <span className="text-xs text-stone-400">Already claimed</span> : <ArrowRight className="w-4 h-4 text-[#064E3B]" />}
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="border-t border-stone-100 pt-4 text-sm text-stone-600">
            Can't find your business? <Link to="/add-business" className="text-[#064E3B] font-semibold hover:underline">Add a new listing →</Link>
          </div>
        </div>
      )}

      {step === 2 && selected && (
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h2 className="font-display text-lg font-semibold mb-1">Step 2 — Verify ownership</h2>
          <p className="text-sm text-stone-500 mb-4">
            You're claiming <strong>{selected.name}</strong> in {selected.city}, {selected.state}.
          </p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Your full name</Label>
              <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="claim-name" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Role in the business</Label>
              <Input value={form.role_in_business} onChange={(e) => setForm({ ...form, role_in_business: e.target.value })} placeholder="Owner / Manager / Medical Director" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase font-semibold tracking-wider">Business email</Label>
                <Input type="email" value={form.business_email} onChange={(e) => setForm({ ...form, business_email: e.target.value })} placeholder="you@yourbusiness.com" />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold tracking-wider">Business phone</Label>
                <Input value={form.business_phone} onChange={(e) => setForm({ ...form, business_phone: e.target.value })} placeholder="(555) 123-4567" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Verification method</Label>
              <Select value={form.verification_method} onValueChange={(v) => setForm({ ...form, verification_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">Phone call to listed number</SelectItem>
                  <SelectItem value="email">Email to business domain</SelectItem>
                  <SelectItem value="document">Upload business license</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Notes (optional)</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional context to help us verify your claim" />
            </div>

            <div className="flex justify-between pt-3 border-t border-stone-100">
              <Button variant="ghost" onClick={() => setStep(1)}>← Back</Button>
              <Button disabled={submitting || !form.full_name} onClick={submit} className="bg-[#064E3B] hover:bg-[#053d2f]" data-testid="claim-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit claim →"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
